import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ModuleId } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useModules } from '../context/ModulesContext';
import ToggleSwitch from '../components/ToggleSwitch';

const iconByModule: Record<ModuleId, string> = {
  core: '🏢',
  collections: '₪',
  analytics: '📊',
  expenses: '🧾',
  orders: '🛒',
  ai_agent: '✦',
};

export default function Modules() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { modules, loading, error, setModuleEnabled } = useModules();
  const [updating, setUpdating] = useState<ModuleId | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const canEdit = user?.role === 'owner' || user?.role === 'admin';

  const toggleModule = async (moduleId: ModuleId, enabled: boolean) => {
    setUpdating(moduleId);
    setUpdateError(null);
    try {
      await setModuleEnabled(moduleId, enabled);
    } catch (err) {
      setUpdateError(t('modules.updateError'));
    } finally {
      setUpdating(null);
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('modules.title')}</h1>
        <p className="mt-1 text-sm text-gray-500">{t('modules.subtitle')}</p>
      </div>

      {!canEdit && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {t('modules.adminOnly')}
        </div>
      )}

      {(error || updateError) && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {updateError || t('modules.loadError')}
        </div>
      )}

      {loading ? (
        <div className="rounded-md border border-gray-200 bg-white p-6 text-sm text-gray-500">
          {t('common.loading')}
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {modules.map((module) => {
            const disabled = module.always_on || !canEdit || updating === module.id;
            return (
              <article key={module.id} className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex min-w-0 items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-gray-100 text-lg text-gray-700">
                      {iconByModule[module.id]}
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-base font-semibold text-gray-900">
                          {t(`modules.${module.id}.name`, module.name)}
                        </h2>
                        {module.always_on && (
                          <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                            {t('modules.alwaysOn')}
                          </span>
                        )}
                        {!canEdit && !module.always_on && (
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                            {t('modules.readOnly')}
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-sm leading-6 text-gray-500">
                        {t(`modules.${module.id}.description`, module.description)}
                      </p>
                      {module.always_on && (
                        <p className="mt-2 text-xs text-gray-400">{t('modules.coreLocked')}</p>
                      )}
                    </div>
                  </div>

                  <ToggleSwitch
                    checked={module.enabled}
                    onChange={(val) => toggleModule(module.id, val)}
                    disabled={disabled}
                    size="lg"
                    aria-label={module.id}
                  />
                </div>
                <div className="mt-4 text-xs font-medium text-gray-500">
                  {module.enabled ? t('modules.enabled') : t('modules.disabled')}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
